/** Viewport dimensions */
import { useSyncExternalStore } from 'react'

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
type OrientationType = 'portrait-primary' | 'portrait-secondary' | 'landscape-primary' | 'landscape-secondary'

const PORTRAIT_TYPES: readonly OrientationType[] = ['portrait-primary', 'portrait-secondary']
const LANDSCAPE_TYPES: readonly OrientationType[] = ['landscape-primary', 'landscape-secondary']

const useMediaQuery = (query: string): boolean => {
  const subscribe = (callback: () => void) => {
    const mql = window.matchMedia(query)
    mql.addEventListener('change', callback)
    return () => mql.removeEventListener('change', callback)
  }
  const getSnapshot = () => window.matchMedia(query).matches
  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}

/**
 * Subscribes to screen.orientation changes so that rotating the device on
 * mobile re-renders components depending on isPortrait / isLandscape.
 * GH #1165 — without this subscription, orientation was a stale snapshot
 * captured at first render.
 */
const useOrientation = (): OrientationType => {
  const subscribe = (callback: () => void) => {
    const target = screen.orientation
    if (!target || typeof target.addEventListener !== 'function') {
      return () => {}
    }
    target.addEventListener('change', callback)
    return () => target.removeEventListener('change', callback)
  }
  const getSnapshot = (): OrientationType => (screen.orientation?.type ?? 'portrait-primary') as OrientationType
  // SSR / non-DOM environments default to portrait-primary.
  return useSyncExternalStore(subscribe, getSnapshot, () => 'portrait-primary' as OrientationType)
}

/** Returns dimensions. Note that isSmall and isMedium  */
export const useDimensions = () => {
  const isXs = useMediaQuery('(max-width:599.95px)')
  const isSm = useMediaQuery('(min-width:600px) and (max-width:899.95px)')
  const isMd = useMediaQuery('(min-width:900px) and (max-width:1199.95px)')
  const isLg = useMediaQuery('(min-width:1200px) and (max-width:1535.95px)')
  const isXl = useMediaQuery('(min-width:1536px)')

  const orientation = useOrientation()

  // GH #1165: the previous form `orientation === 'portrait-primary' || 'portrait-secondary'`
  // is `(boolean) || (string)`, which evaluates to the truthy string when the
  // first arm is false — so isPortrait/isLandscape were always truthy.
  const isPortrait = PORTRAIT_TYPES.includes(orientation)
  const isLandscape = LANDSCAPE_TYPES.includes(orientation)

  const isTablet = isMd || isSm
  const isDesktop = isLg || isXl

  let size: Breakpoint
  if (isXs) {
    size = 'xs'
  } else if (isSm) {
    size = 'sm'
  } else if (isMd) {
    size = 'md'
  } else if (isLg) {
    size = 'lg'
  } else {
    size = 'xl'
  }

  return {
    size,
    isTiny: isXs && !isSm,
    isSmallOrSmaller: isXs || isSm,
    isSmall: isSm,
    isMediumOrSmaller: isMd || isSm || isXs,
    isMedium: isMd,
    isLargeOrSmaller: isLg || isMd || isSm || isXs,
    isLarge: isLg,
    isXLargeOrSmaller: isXl || isLg || isMd || isSm || isXs,
    isXLarge: isXl,
    isPortrait,
    isLandscape,
    orientation,
    isDesktop,
    isTablet,
  }
}
